import { useEffect, useRef } from "react";
import { useEditorStore } from "../../state/editorStore";
import { resolveCreationImageUrl } from "../../utils/resolveCreationImageUrl";

/**
 * Keeps React processedImage + canvas background layer in sync when undo/redo
 * (or store imageUrl) changes Zustand without re-running AI.
 */
export function useEditorUndoSync({
  setProcessedImage,
  setCreationId,
  getToken,
  canvasEditorRef,
  enabled = true,
}) {
  const storeImageUrl = useEditorStore((s) => s.imageUrl);
  const storeCreationId = useEditorStore((s) => s.creationId);
  const background = useEditorStore((s) => s.background);
  const pastLength = useEditorStore((s) => s.past.length);
  const futureLength = useEditorStore((s) => s.future.length);

  const lastSyncedImageRef = useRef(null);
  const lastBgKeyRef = useRef(null);
  const historyTickRef = useRef({ past: 0, future: 0 });

  // After undo/redo: re-resolve image from creation API when possible (fresh blob).
  useEffect(() => {
    if (!enabled) return;

    const prev = historyTickRef.current;
    const isUndo = pastLength < prev.past;
    const isRedo = futureLength < prev.future;
    historyTickRef.current = { past: pastLength, future: futureLength };

    if (!isUndo && !isRedo) return;

    let cancelled = false;

    (async () => {
      const state = useEditorStore.getState();
      const cid = state.creationId;
      const imageUrl = state.imageUrl;
      if (!imageUrl && !cid) return;

      let displayUrl = imageUrl;
      const token = getToken ? await getToken() : null;

      if (cid && token) {
        try {
          const resolved = await resolveCreationImageUrl(token, imageUrl, cid);
          displayUrl = resolved.displayUrl;
        } catch {
          // Keep snapshot URL if API fetch fails.
        }
      }

      if (cancelled || !displayUrl) return;

      if (displayUrl !== state.imageUrl) {
        useEditorStore.getState().setImageUrl(displayUrl, { recordHistory: false });
      }

      lastSyncedImageRef.current = displayUrl;
      setProcessedImage(displayUrl);
      if (setCreationId) {
        setCreationId(cid ?? null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    pastLength,
    futureLength,
    setProcessedImage,
    setCreationId,
    getToken,
  ]);

  // Forward edits (non-undo): sync store imageUrl → React.
  useEffect(() => {
    if (!enabled || storeImageUrl == null) return;
    if (lastSyncedImageRef.current === storeImageUrl) return;

    lastSyncedImageRef.current = storeImageUrl;
    setProcessedImage(storeImageUrl);
    if (setCreationId) {
      setCreationId(storeCreationId ?? null);
    }
  }, [
    enabled,
    storeImageUrl,
    storeCreationId,
    setProcessedImage,
    setCreationId,
  ]);

  useEffect(() => {
    if (!enabled) return;

    const bgType = background?.type ?? null;
    const bgKey = `${bgType}|${background?.imageUrl ?? ""}|${background?.color ?? ""}`;
    if (lastBgKeyRef.current === bgKey) return;
    lastBgKeyRef.current = bgKey;

    const canvas = canvasEditorRef.current;
    if (!canvas) return;

    if (bgType === "image" && background.imageUrl) {
      canvas.setBackgroundImage?.(background.imageUrl);
    } else if (bgType === "ai") {
      canvas.removeBackgroundImage?.();
    } else if (bgType === "color" || bgType === null) {
      canvas.removeBackgroundImage?.();
    }
  }, [
    enabled,
    background?.type,
    background?.imageUrl,
    background?.color,
    canvasEditorRef,
    pastLength,
    futureLength,
  ]);
}
